// Finds the invoice or fee slip document that belongs to a row on a clerk statement.
//
// A statement row names an invoice number but carries no document. The Render service can
// match that number against invoices it has already seen, returning the Gmail attachment it
// arrived on. That is what makes it possible to send someone the actual fee slips for a
// group of statement rows rather than just a list of numbers.
//
// Both requests go through /api/render/... and /api/gmail/..., which vercel.json routes to
// the authenticated signing proxy. Neither ever addresses Render directly.

export const MATCH_INVOICES_PATH = "/api/render/api/clerk-statements/match-invoices";
export const MATCH_INVOICES_LIVE_PATH = "/api/render/api/clerk-statements/match-invoices-live";

// The database lookup above is fast but only knows invoices the service has already synced.
// The Client Account dashboard also searches Gmail in real time for the numbers the database
// missed, which is why it attached more documents than this screen did. That second pass now
// runs here too, but as a follow-up: the fast lookup renders first, then the live search
// fills in the gaps, so a 79-row statement is never held behind a Gmail query.
//
// The service hard-limits a live call to 15 numbers and gives up after 12 seconds, returning
// an empty result rather than an error. Requests are therefore chunked, and a chunk that
// finds nothing is simply a chunk that adds nothing. One slow number makes its whole chunk
// time out: on Holmes' List (16 Oct 2026) chunks of 15 and then of 5 each came back empty
// after ~12.3 s, while every one of those numbers was found when asked for alone (1-12 s
// each). So each number is its own call; results still appear as each one returns.
export const LIVE_BATCH_SIZE = 1;

// A broad search can return every email that mentions an invoice: 102 attachments for one
// Holmes invoice. A row shows at most this many, preferring files named with the number.
export const MAX_ATTACHMENTS_PER_INVOICE = 3;

const cleanInvoiceNumber = (value) => String(value ?? "").trim();

// "06012508", "6012508" and "Invoice 06012508" are the same invoice.
const invoiceDigits = (value) => {
  const digits = String(value ?? "").replace(/\D/g, "").replace(/^0+/, "");
  return digits.length >= 4 ? digits : "";
};

// The email intake files each emailed invoice's PDF in the portal's own storage, with the
// invoice number read off it. A statement row with the same number has its invoice already:
// no lookup, no Gmail search. Several emails can carry one invoice (reminders), so each file
// name is listed once. Returns Map(statement invoice number -> attachments).
export function filedInvoiceMatches(rows = []) {
  const filed = new Map();
  for (const row of rows) {
    if (row?.source !== "email" || !row.documentUrl) continue;
    const key = invoiceDigits(row.invoiceRef);
    if (!key) continue;
    if (!filed.has(key)) filed.set(key, []);
    const list = filed.get(key);
    const filename = cleanInvoiceNumber(row.invoiceName) || "Invoice.pdf";
    if (list.some((attachment) => attachment.filename.toLowerCase() === filename.toLowerCase())) continue;
    list.push({
      filename,
      mimeType: "application/pdf",
      size: Number(row.invoiceSize) || 0,
      sourceMessageId: "",
      url: row.documentUrl,
      meta: "Filed from email",
    });
  }
  const matches = new Map();
  for (const row of rows) {
    if (row?.source !== "clerk_statement") continue;
    const number = cleanInvoiceNumber(row.invoiceName);
    const found = filed.get(invoiceDigits(number));
    if (found?.length) matches.set(number, found);
  }
  return matches;
}

export function collectInvoiceNumbers(statements = []) {
  const numbers = statements
    .map((item) => cleanInvoiceNumber(item?.invoiceName))
    .filter((value) => value && value !== "No invoice number");
  return [...new Set(numbers)];
}

// The service answers with two kinds of match. A Gmail hit carries the message it arrived
// on, and the link is built from that. An inbox hit carries no message id at all - it is
// addressed by attachment id - and arrives with its own ready-made url. Building the Gmail
// link was previously the only path, so every inbox match produced an empty url and was
// dropped a few lines below: documents the service had found were silently discarded before
// they ever reached the screen.
//
// A url off the wire is only trusted if it is a same-origin API path. Anything absolute,
// protocol-relative or outside /api/ is ignored rather than rendered as a link.
const isSafeApiPath = (value) => /^\/api\/[^/]/.test(value);

// The service's own paths are reached through the signing proxy. A few are routed there by
// vercel.json under their own name; the rest - an inbox document, or a firm's signed invoice
// link (/api/clerk-statements/attachment) - would 404 unless sent through /api/render/.
const PROXIED_BY_NAME = /^\/api\/(?:gmail|fee-slips|lacw-emails|render)(?:[/?]|$)/;
const throughProxy = (value) => (PROXIED_BY_NAME.test(value) ? value : `/api/render${value}`);

export function buildAttachmentUrl(attachment = {}) {
  const provided = cleanInvoiceNumber(attachment.url);
  if (provided && isSafeApiPath(provided)) return throughProxy(provided);

  const messageId = cleanInvoiceNumber(attachment.source_message_id);
  const filename = cleanInvoiceNumber(attachment.filename);
  if (!messageId || !filename) return "";
  return `/api/gmail/messages/${encodeURIComponent(messageId)}/attachments?filename=${encodeURIComponent(filename)}`;
}

export function describeAttachment(attachment = {}) {
  const parts = [];
  if (attachment.mime_type) parts.push(String(attachment.mime_type));
  const size = Number(attachment.size);
  if (Number.isFinite(size) && size > 0) parts.push(`${Math.max(1, Math.round(size / 1024))} KB`);
  return parts.join(" · ");
}

// The attachments worth showing for one invoice: those whose file name carries its number,
// or, if none does, the first few.
export function pickAttachments(invoiceNumber, attachments = []) {
  const digits = invoiceDigits(invoiceNumber);
  const named = digits
    ? attachments.filter((attachment) => String(attachment.filename || "").replace(/\D/g, "").includes(digits))
    : [];
  return (named.length ? named : attachments).slice(0, MAX_ATTACHMENTS_PER_INVOICE);
}

export function normaliseMatches(matches = {}) {
  const byInvoice = new Map();
  for (const [invoiceNumber, list] of Object.entries(matches || {})) {
    const all = (Array.isArray(list) ? list : [])
      .map((attachment) => ({
        filename: cleanInvoiceNumber(attachment?.filename),
        mimeType: cleanInvoiceNumber(attachment?.mime_type),
        size: Number(attachment?.size) || 0,
        sourceMessageId: cleanInvoiceNumber(attachment?.source_message_id),
        url: buildAttachmentUrl(attachment),
        meta: describeAttachment(attachment),
      }))
      .filter((attachment) => attachment.url);
    const attachments = pickAttachments(invoiceNumber, all);
    if (attachments.length) byInvoice.set(cleanInvoiceNumber(invoiceNumber), attachments);
  }
  return byInvoice;
}

// Numbers the database pass returned nothing for. These are what the live search is for.
export function unmatchedInvoiceNumbers(invoiceNumbers = [], matches = new Map()) {
  return invoiceNumbers
    .map(cleanInvoiceNumber)
    .filter((number) => number && !(matches.get(number) || []).length);
}

export function chunkInvoiceNumbers(invoiceNumbers = [], size = LIVE_BATCH_SIZE) {
  const numbers = invoiceNumbers.map(cleanInvoiceNumber).filter(Boolean);
  const chunks = [];
  for (let index = 0; index < numbers.length; index += size) {
    chunks.push(numbers.slice(index, index + size));
  }
  return chunks;
}

// Later results never overwrite an earlier hit: the database match is the cheaper, more
// reliable one, and the live pass is only ever filling in blanks.
export function mergeMatchMaps(base = new Map(), extra = new Map()) {
  const merged = new Map(base);
  for (const [invoiceNumber, attachments] of extra) {
    if (!(merged.get(invoiceNumber) || []).length && attachments.length) {
      merged.set(invoiceNumber, attachments);
    }
  }
  return merged;
}

async function postInvoiceNumbers(path, numbers, fetchImpl) {
  const response = await fetchImpl(path, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ invoice_numbers: numbers }),
  });
  if (!response.ok) throw new Error(`Invoice matching returned ${response.status}`);
  const payload = await response.json();
  if (payload?.ok === false) throw new Error(payload.error || "Invoice matching unavailable");
  return normaliseMatches(payload?.matches);
}

// Searches Gmail for the numbers the database did not know, one chunk at a time. Chunks run
// in sequence rather than at once: each is a live Gmail query on the service's single
// account, and firing seven of them together is how that account gets rate-limited.
//
// onChunk, if given, is called with the merged map after each chunk, so documents appear as
// they are found instead of after the last chunk returns. A chunk that throws is logged and
// skipped - a Gmail outage must not cost the results the other chunks found.
export async function fetchInvoiceMatchesLive(
  invoiceNumbers = [],
  { fetchImpl = globalThis.fetch, onChunk, batchSize = LIVE_BATCH_SIZE } = {},
) {
  let found = new Map();
  for (const chunk of chunkInvoiceNumbers(invoiceNumbers, batchSize)) {
    try {
      const matches = await postInvoiceNumbers(MATCH_INVOICES_LIVE_PATH, chunk, fetchImpl);
      if (!matches.size) continue;
      found = mergeMatchMaps(found, matches);
      if (onChunk) onChunk(found);
    } catch (error) {
      console.warn("Live invoice search failed for a batch:", error);
    }
  }
  return found;
}

export async function fetchInvoiceMatches(invoiceNumbers = [], fetchImpl = globalThis.fetch) {
  const numbers = invoiceNumbers.map(cleanInvoiceNumber).filter(Boolean);
  if (!numbers.length) return new Map();
  return postInvoiceNumbers(MATCH_INVOICES_PATH, numbers, fetchImpl);
}
